import React, { useEffect, useMemo, useState } from "react";
import "../../styles/glass.css";
import "../../styles/globals.css";
import "../../styles/layout.css";
import "../styles/platform.css";
import "../styles/LeftSidebar.css";
import HeaderPlatform from "../components/HeaderPlatform";
import FooterPlatform from "../components/FooterPlatform";
import LeftSidebar from "../components/LeftSidebar";
import GlassCard from "../../components/GlassCard";
import { useCurrentLang } from "../../hooks/useCurrentLang";
import DisplayRow from "../components/DisplayRow";
import { useNavigate } from "react-router-dom";
import { historyText } from "../data/platformText";
import { getSessionHistory } from "../api/planApi";
import LoadingPage from "./LoadingPage";
import ErrorPage from "./ErrorPage";

export const MyHistoryPage = () => {
  const navigate = useNavigate();
  const currentLang = useCurrentLang();
  const t = historyText[currentLang];

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadHistory() {
      try {
        setLoading(true);
        setError("");
        const data = await getSessionHistory();

        const mapped = (data || []).map((session) => {
          const seconds = session.estimated_time ? Number(session.estimated_time) * 60 : 0;

          return {
            id: session.id,
            name: session.name ?? `Session ${session.id}`,
            difficulty: session.hardness_score ?? "-",
            numberOfSets: 0,
            length: formatSeconds(seconds),
            completed: session.status === "completed",
            date: formatDate(session.scheduled_date),
            lengthSeconds: seconds,
          };
        });

        setSessions(mapped);
      } catch (err) {
        setError(err.message || "Failed to load history");
      } finally {
        setLoading(false);
      }
    }

    loadHistory();
  }, []);

  const parseDate = (dateStr) => {
    const [day, month, year] = dateStr.split(".");
    return new Date(+year, +month - 1, +day);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const d = new Date(dateValue);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const groupSessionsByDate = (sessions) => {
    const groups = sessions.reduce((acc, session) => {
      if (!acc[session.date]) acc[session.date] = [];
      acc[session.date].push(session);
      return acc;
    }, {});

    const sortedDates = Object.keys(groups).sort(
      (a, b) => parseDate(b) - parseDate(a)
    );

    return sortedDates.map((date) => ({
      date,
      sessions: groups[date],
    }));
  };

  const groupedSessions = useMemo(
    () => groupSessionsByDate(sessions),
    [sessions]
  );

  const handleGoToSession = (session) => {
    navigate({
      pathname: `/${currentLang}/platform/sessions/${session.id}`,
    });
  };

  const earliestSession = useMemo(() => {
    if (sessions.length === 0) return null;
    return sessions.reduce((earliest, session) =>
      parseDate(session.date) < parseDate(earliest.date) ? session : earliest
    );
  }, [sessions]);

  const totalSets = sessions.reduce((total, session) => total + session.numberOfSets, 0);

  function formatSeconds(seconds) {
    const safe = Number(seconds || 0);
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const secs = safe % 60;

    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  const formatDuration = (seconds, currentLang) => {
    const safe = Number(seconds || 0);
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);

    const isSl = currentLang === "sl";

    const getHourLabel = (value) => {
      if (isSl) {
        if (value === 1) return "uro";
        if (value === 2) return "uri";
        if (value === 3 || value === 4) return "ure";
        return "ur";
      }

      return `hour${value !== 1 ? "s" : ""}`;
    };

    const getMinuteLabel = (value) => {
      if (isSl) {
        if (value === 1) return "minuto";
        if (value === 2) return "minuti";
        if (value === 3 || value === 4) return "minute";
        return "minut";
      }

      return `minute${value !== 1 ? "s" : ""}`;
    };

    const andWord = isSl ? "in" : "and";

    if (hours > 0 && minutes > 0) {
      return `${hours} ${getHourLabel(hours)} ${andWord} ${minutes} ${getMinuteLabel(minutes)}`;
    }

    if (hours > 0) {
      return `${hours} ${getHourLabel(hours)}`;
    }

    return `${minutes} ${getMinuteLabel(minutes)}`;
  };

  const totalWorkoutSeconds = sessions.reduce((sum, s) => sum + s.lengthSeconds, 0);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage err={error} />;
  }

  return (
    <div className="revimo-layout">
      <HeaderPlatform />
      <main className="revimo-main">
        <LeftSidebar />
        <div className="revimo-page-content">
          <GlassCard style={{ padding: "20px", maxWidth: "775px", marginLeft: "50px" }}>
            <div className="profile-section header-section">
              <div className="profile-col profile-col-left">
                <div style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.7 }}>
                  {t.s}
                </div>
                <div style={{ textAlign: "start", paddingBottom: "5px", fontWeight: "500" }}>
                  <div style={{ margin: "5px", fontWeight: "600", fontSize: "26px", maxWidth: "500px" }}>
                    {t.t}
                  </div>
                  <div className="profile-main-username"></div>
                  <div className="profile-main-sex"></div>
                </div>
              </div>
            </div>

            <div style={{ height: "20px" }}></div>
            <div style={{ color: "var(--text-muted)" }}>{t.desc1}</div>
            <div style={{ marginTop: "10px", color: "var(--text-muted)" }}>{t.desc2}</div>
          </GlassCard>

          <GlassCard
            style={{
              padding: "20px",
              borderRadius: "24px",
              marginTop: "24px",
              marginLeft: "50px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 20,
                    marginBottom: 4,
                  }}
                >
                  {t.gj} {formatDuration(totalWorkoutSeconds)} {t.exe}
                </div>
              </div>
            </div>
          </GlassCard>

          <GlassCard style={{ padding: "20px", maxWidth: "775px", marginLeft: "50px", marginTop: "24px" }}>
            <div style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.7, marginBottom: "10px" }}>
              {t.history}
            </div>

            <div className="profile-section">
              <div className="profile-col profile-col-left">
                <DisplayRow label={t.complSess} value={sessions.length} />
                <DisplayRow label={t.complSets} value={totalSets} />
              </div>

              <div className="profile-col profile-col-right">
                <DisplayRow label={t.Tspent} value={formatSeconds(totalWorkoutSeconds)} />
                <DisplayRow label={t.fSessOn} value={earliestSession?.date ?? "-"} />
              </div>
            </div>
          </GlassCard>

          <div
            style={{
              maxWidth: "775px",
              marginLeft: "50px",
              marginTop: "24px",
            }}
          >
            <div
              style={{
                fontSize: "0.8rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                opacity: 0.7,
                marginBottom: "10px",
              }}
            >
              {t.sessHistory}
            </div>

            {groupedSessions.length === 0 && (
              <GlassCard style={{ padding: "20px" }}>
                No completed sessions yet.
              </GlassCard>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {groupedSessions.map((group) => (
                <GlassCard
                  key={group.date}
                  style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      opacity: 0.7,
                      marginBottom: "10px",
                    }}
                  >
                    {group.date}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {group.sessions.map((session) => (
                      <div
                        key={session.id}
                        style={{
                          border: "none",
                          background: "rgba(255,255,255,0.28)",
                          borderRadius: "16px",
                          padding: "14px 16px",
                          cursor: "pointer",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            width: "250px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "1rem",
                              fontWeight: 600,
                              color: "#0b1720",
                            }}
                          >
                            {session.name}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.92rem",
                              color: "#0b1720",
                            }}
                          >
                            {t.diff} {session.difficulty}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.92rem",
                              color: "#0b1720",
                            }}
                          >
                            {t.sets} {session.numberOfSets}
                          </span>
                        </div>

                        <span
                          style={{
                            fontSize: "0.92rem",
                            color: "#0b1720",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {session.length}
                        </span>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              ))}
            </div>
          </div>
        </div>
      </main>

      <footer className="revimo-footer">
        <FooterPlatform />
      </footer>
    </div>
  );
};

export default MyHistoryPage;